import currencyLib from "currency.js";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import {
  selectAppliedOfferResponse,
  selectCartDeliveryFees,
  selectCartDiscount,
  selectCartPoints,
  selectCartService,
  selectCartSubtotal,
  selectCartTax,
  selectCartTotal,
  selectIsOfferApplied,
  selectOfferAppliedLineIds,
} from "../../../services/redux/selectors/cartSelectors";
import { selectUseLoyaltyService } from "../../../services/redux/selectors/loyaltyServiceSelectors";
import { selectRedeemedLoyaltyOffer } from "../../../services/redux/selectors/loyaltyOfferSelectors";
import {
  selectPayWithPointsApplied,
  selectReceiptPayWithPointsAmount,
} from "../../../services/redux/selectors/payWithPointsSelectors";
import {
  BogoOfferPayload,
  LocalizedString,
} from "../../../services/redux/types/CartLoyaltyV2Types";
import Offer from "../../../utils/OfferClass/Offer/Offer";
import { createOffer } from "../../../utils/OfferClass/OfferClassUtils/OfferCreator/offerCreator";
import {
  OrderInfoScreenView,
  OrderTypesEnum,
  PAYMENT_METHOD,
} from "../../../utils/enums";
import env from "../../../utils/env.config";
import {
  isArabicLanguage,
  localizedNumber,
} from "../../../utils/help/localization/localization";
import { findLegacyRedeemedOffer } from "../../../utils/help/offers/findLegacyRedeemedOffer";
import { buildEarnedPointsLabel } from "../../../utils/help/offers/multiplierBannerUtils";
import { subtractWithRoundingOrCurrency } from "../../../utils/help/maths/maths";
import {
  CustomerPackageOffer,
  PreferredCountryEnum,
} from "../../../utils/types";
import styles from "./BillTable.module.scss";

export const BillTable = (props: {
  total?: number;
  rows?: any;
  tips?: number;
  paidPrice?: number;
  service?: number;
  discountedValue?: any;
  customerPackageOffer?: CustomerPackageOffer;
  paymentMethod?: PAYMENT_METHOD;
  hideThankYou?: boolean;
  screenView?: OrderInfoScreenView;
  vat?: number;
  deliveryCosts?: number;
  subtotal?: number;
  serviceDb?: number;
}) => {
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );

  const delivery = useAppSelector((state) => state.delivery);
  const { t } = useTranslationI18();
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);

  const [finalRows, setFinalRows] = useState<any>([]);
  const subtotalTranslation = t("common:global-subtotal");
  const serviceTranslation = t("common:global-service");
  const totalTranslation = t("common:global-total");
  const tipsTranslation = t("common:global-tips");
  const TotalBill = t("common:global-total-bill");
  const deliveryFee = t("delivery:delivery-fee");
  const alreadyIncluded = t("common:already-included");
  const vatIncluded = t("common:vat-included");
  const youEarned = t("offers:you-earned");
  const youSaved = t("offers:you-saved");
  const pointsDiscountTranslation = t("pointing-system:points-discount");
  const cartAndLoyaltyReducer = useAppSelector(
    (state) => state.cartAndLoyaltyReducer
  );
  const cartV2 = useAppSelector((state) => state.cartLoyaltyV2Reducer).cart;
  const useLoyaltyService = useAppSelector(selectUseLoyaltyService);
  const cartSubtotal = useAppSelector(selectCartSubtotal);
  const cartTotal = useAppSelector(selectCartTotal);
  const cartTax = useAppSelector(selectCartTax);
  const cartService = useAppSelector(selectCartService);
  const cartDeliveryFees = useAppSelector(selectCartDeliveryFees);
  const cartDiscount = useAppSelector(selectCartDiscount);
  const isOfferApplied = useAppSelector(selectIsOfferApplied);
  const offerAppliedLineIds = useAppSelector(selectOfferAppliedLineIds);
  const cartPoints = useAppSelector(selectCartPoints);
  const payWithPointsApplied = useAppSelector(selectPayWithPointsApplied);
  const receiptPayWithPointsAmount = useAppSelector(
    selectReceiptPayWithPointsAmount
  );

  // ----------------------Receipt fallback stays scoped to the pay screen: a stale payScreenReceipt would leak a discount row onto a fresh cart-----------------------
  const payWithPointsAmount =
    payWithPointsApplied && payWithPointsApplied.amount > 0
      ? payWithPointsApplied.amount
      : props.screenView === OrderInfoScreenView.Pay
      ? receiptPayWithPointsAmount
      : undefined;
  const appliedOfferResponse = useAppSelector(selectAppliedOfferResponse);
  const redeemedLoyaltyOffer = useAppSelector(selectRedeemedLoyaltyOffer);

  const redeemedOffer = !useLoyaltyService
    ? findLegacyRedeemedOffer(
        cartAndLoyaltyReducer.offersArray,
        cartV2.redeemedOffer
      )
    : undefined;

  const currency = useAppSelector((state) => state.config.currency);
  const isLanguageArabic = isArabicLanguage();

  const memoizedRows = useMemo(() => {
    if (!props.rows) {
      return [];
    }

    return props.rows.map((row: any) => ({
      name: row.name,
      value: row.value,
      promotionStyle: row.promotionStyle,
    }));
  }, [props.rows]);

  useEffect(() => {
    let allRows: any[] = [];

    if (restaurantInfo) {
      const hasRoundingMethodAndLevel =
        !!restaurantInfo?.currencyRoundingLevel &&
        !!restaurantInfo?.currencyRoundingMethod;

      const allPrices = useLoyaltyService
        ? {
            subTotal: cartSubtotal,
            paidPrice: cartTotal,
            vat: cartTax,
            service: cartService,
            deliveryFees: cartDeliveryFees,
            discount: cartDiscount,
            subTotalWithDiscount: subtractWithRoundingOrCurrency(
              hasRoundingMethodAndLevel,
              cartSubtotal,
              cartDiscount
            ),
          }
        : {
            subTotal: cartV2.subTotalPrice,
            paidPrice: cartV2.totalPrice,
            vat: cartV2.vat,
            service: cartV2.service,
            deliveryFees: cartV2.deliveryFees,
            discount: cartV2.discountedValue,
            subTotalWithDiscount: subtractWithRoundingOrCurrency(
              hasRoundingMethodAndLevel,
              cartV2.subTotalPrice,
              cartV2.discountedValue
            ),
          };

      if (props.rows === undefined) {
        allRows = [
          {
            name: subtotalTranslation,
            value:
              currencyLib(props.subtotal!).value ||
              currencyLib(allPrices.subTotal).value ||
              currencyLib(props.total!).value,
          },
        ];

        const applyDiscountAfterVat =
          restaurantInfo?.config?.applyDiscountAfterVat;

        const vatValue =
          currencyLib(props.vat!).value ||
          currencyLib(allPrices.vat!).value ||
          0;
        const shouldHideVat = restaurantInfo?.config?.hideVat && vatValue === 0;
        // A zero VAT is shown as "Incl." rather than a 0 amount when the
        // restaurant opts in. `hideVat` still wins — it drops the row entirely.
        const shouldShowVatIncluded =
          restaurantInfo?.config?.showVatIncludedWhenZero && vatValue === 0;
        const vatRowValue = shouldShowVatIncluded
          ? vatIncluded
          : currencyLib(props.vat!).value === 0 &&
            currencyLib(allPrices.vat!).value === 0 &&
            env.enviroment === "sa"
          ? alreadyIncluded
          : vatValue;

        if (applyDiscountAfterVat && !shouldHideVat) {
          allRows.push({
            name:
              restaurantInfo?.preferredCountry === PreferredCountryEnum.CA
                ? t("common:global-hst")
                : t("common:global-vat"),
            value: vatRowValue,
          });
        }

        if (props.customerPackageOffer) {
          const offer = createOffer(props.customerPackageOffer.packageOffer);

          const name =
            props.discountedValue && props.discountedValue > 0
              ? youSaved
              : youEarned;
          const value =
            props.discountedValue && props.discountedValue > 0
              ? currencyLib(props.discountedValue).value.toFixed(2)
              : Offer.getLocalizedValue(offer?.uiDetails?.titleText);

          allRows.push({
            name,
            value,
            promotionStyle: true,
          });
        } else if (
          props.discountedValue > 0 &&
          (props.screenView === OrderInfoScreenView.OrderSummary ||
            props.screenView === OrderInfoScreenView.Pay)
        ) {
          allRows.push({
            name: youSaved,
            value: currencyLib(props.discountedValue).value.toFixed(2),
            promotionStyle: true,
          });
        } else {
          if (
            !useLoyaltyService &&
            props.screenView === OrderInfoScreenView.Cart &&
            cartV2.redeemedOffer &&
            cartV2.isEligibleOffer
          ) {
            const offerRedeemed = findLegacyRedeemedOffer(
              cartAndLoyaltyReducer.offersArray,
              cartV2.redeemedOffer
            );

            const isApplicable = offerRedeemed?.isOfferApplicable({
              cart: cartV2,
              itemStatusRedux,
              orderType: orderTypeParam,
              timeZone: restaurantInfo?.timeZone!,
            });

            if (isApplicable) {
              const name = cartV2.discountedValue > 0 ? youSaved : youEarned;
              const value =
                cartV2.discountedValue > 0
                  ? cartV2.discountedValue
                  : Offer.getLocalizedValue(
                      (
                        (cartV2.redeemedOffer as BogoOfferPayload).getOffer
                          ?.uiDetails ?? offerRedeemed?.uiDetails
                      )?.titleText
                    )!;

              allRows.push({
                name,
                value,
                promotionStyle: true,
              });
            }
          } else if (
            useLoyaltyService &&
            props.screenView === OrderInfoScreenView.Cart &&
            (isOfferApplied ||
              (offerAppliedLineIds.length > 0 && cartDiscount > 0))
          ) {
            const name = cartDiscount > 0 ? youSaved : youEarned;
            // A multiplier earns points rather than a discount — show the boosted
            // point total instead of the offer title.
            const earnedPointsLabel =
              (cartPoints?.earned?.bonus ?? 0) > 0
                ? buildEarnedPointsLabel(t, cartPoints?.earned?.total)
                : "";
            const value =
              cartDiscount > 0
                ? cartDiscount
                : earnedPointsLabel ||
                  Offer.getLocalizedValue(
                    redeemedLoyaltyOffer?.uiConfig?.title as LocalizedString
                  )!;

            allRows.push({
              name,
              value,
              promotionStyle: true,
            });
          }
        }

        if (payWithPointsAmount && payWithPointsAmount > 0) {
          allRows.push({
            name: pointsDiscountTranslation,
            value: -payWithPointsAmount,
            promotionStyle: true,
          });
        }

        if (props.serviceDb || props.service) {
          allRows.push({
            name: serviceTranslation,
            value:
              currencyLib(props.serviceDb!).value ||
              currencyLib(allPrices.service!).value ||
              (props.service && currencyLib(props.service).value),
          });
        }

        if (!applyDiscountAfterVat && !shouldHideVat) {
          allRows.push({
            name:
              restaurantInfo?.preferredCountry === PreferredCountryEnum.CA
                ? t("common:global-hst")
                : t("common:global-vat"),
            value: vatRowValue,
          });
        }

        if (
          !props.deliveryCosts &&
          orderTypeParam === OrderTypesEnum.DELIVERYV2 &&
          delivery.currentDeliveryConfig &&
          allPrices.deliveryFees &&
          props.screenView !== OrderInfoScreenView.Pay
        ) {
          allRows.push({
            name: deliveryFee,
            value: allPrices.deliveryFees,
          });
        }

        if (props.deliveryCosts) {
          allRows.push({
            name: deliveryFee,
            value: props.deliveryCosts,
          });
        }

        if (
          props.paymentMethod &&
          props.paymentMethod === PAYMENT_METHOD.ONLINE_CARD &&
          props.tips
        ) {
          allRows.push({
            name: tipsTranslation,
            value: currencyLib(props.tips).value,
          });
        }

        allRows.push({
          name: totalTranslation,
          value:
            props.paidPrice && props.paymentMethod
              ? currencyLib(props.paidPrice).value
              : allPrices.paidPrice,
        });
      } else {
        allRows = props.rows;
      }
    }

    setFinalRows(allRows);
  }, [
    props.total,
    props.subtotal,
    props.serviceDb,
    props.service,
    props.vat,
    props.tips,
    props.paidPrice,
    props.customerPackageOffer?.id,
    props.screenView,
    props.discountedValue,
    props.hideThankYou,
    props.deliveryCosts,
    cartV2.cartItemsNumber,
    cartV2.totalPrice,
    useLoyaltyService,
    cartSubtotal,
    cartTotal,
    cartTax,
    cartService,
    cartDeliveryFees,
    cartDiscount,
    isOfferApplied,
    offerAppliedLineIds.length,
    cartPoints?.earned?.bonus,
    payWithPointsAmount,
    appliedOfferResponse?.status,
    appliedOfferResponse?.reason?.message,
    redeemedLoyaltyOffer?.id,
    redeemedOffer?.id,
    orderTypeParam,
    delivery.deliveryFees,
    restaurantInfo?.id,
    memoizedRows,
  ]);

  return (
    <>
      {props.rows !== undefined && (
        <p className={styles.bill_title}>{TotalBill}</p>
      )}
      <div className={styles.table_wrapper} data-testid={"bill-table"}>
        {finalRows.map((row: any, index: number) => (
          <div
            key={row.name}
            className={`${styles.table_wrapper__row} ${
              row.promotionStyle && styles.discount
            }`}
            role={`bill-table-row-${index}`}
            data-testid={row.name.replace(/ /g, "-") + "-testid"}
          >
            <div className={`${styles.cell_name}`}>{row.name}</div>
            <div
              data-testid={`bill-table-row-value-${index}`}
              role={row["data-testid"] && row["data-testid"]}
              data-testtotalvalue={
                row["data-testtotalvalue"] && row["data-testtotalvalue"]
              }
              className={styles.cell_value}
            >
              {isNaN(row.value)
                ? row.value
                : isLanguageArabic
                ? `${localizedNumber(Math.abs(row.value))}${
                    row.value < 0 ? "-" : ""
                  } ${currency!}`
                : `${row.value < 0 ? "-" : ""}${currency!}${localizedNumber(
                    Math.abs(row.value)
                  )}`}
            </div>
          </div>
        ))}
      </div>
      {/* {props.rows && !props.hideThankYou && (
        <p
          className={styles.waiter_thanks}
          style={{
            visibility: props?.rows[1]?.value > 0 ? "visible" : "hidden",
            height: "auto",
            minHeight: "1.5em",
          }}
        >
          {props?.rows[1]?.value > 0 ? (
            <>
              {thankYouTranslation}
              {user?.length > 0 ? `,  ${user}` : ""}
            </>
          ) : (
            "\u00A0"
          )}
        </p>
      )} */}
    </>
  );
};
